import { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Empty, Input, Popconfirm, Row, Select, Space, Table, Tag, Typography, message } from "antd";
import { FilterOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, TeamOutlined, UserDeleteOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { ModalComponent, CancelButton } from "@comps/components";
import { makeRequest, makeRequestStateless } from "@utils/helpers";
import { getEmployeesReq, getSalaryPolicyByEmpId, updateSalaryPolicyReq } from "../../employeeManagement/companyEmployees/requests";
import { getResourceDataReq } from "@comps/commonFunctions";
import FilterSelectEmployees from "@mods/common/FilterSelectEmployees";
import { getCompanyRelatedDataReq } from "@mods/common/requests";
import UpdatePayrollPolicy from "../../employeeManagement/companyEmployees/components/UpdatePayrollPolicy";
import { bulkAssignPayrollEmployeesReq } from "./requests";
import "../../rosterManagement/common/Roster.css";

const { Text } = Typography;

const mapSalaryBenefits = (salaryBenefits = []) => (
  salaryBenefits.map((item) => ({
    grade_benefit_id: item.grade_benefit_id,
    benefit_group_ids: item.benefit_group_ids || [],
    benefit_details: typeof item.benefit_details === "string"
      ? item.benefit_details
      : JSON.stringify(item.benefit_details || []),
  }))
);

const PayrollEmployeePolicyEditorModal = ({ employmentId, onClose, onRefresh }) => (
  <ModalComponent
    top={ 20 }
    width={ 1200 }
    maskClosable={ false }
    visible={ true }
    footer={ [<CancelButton key="close_button" onClick={ onClose } />] }
    onCancel={ onClose }
    mainTitle="Manage Employee Payroll"
    subTitle="Update payroll assignment and policy"
  >
    <UpdatePayrollPolicy key={ employmentId } id={ employmentId } />
    <div style={ { display: "flex", justifyContent: "flex-end", marginTop: 16 } }>
      <Button type="primary" onClick={ () => {
        onRefresh?.();
        onClose();
      } }>
        Done
      </Button>
    </div>
  </ModalComponent>
);

const PayrollBulkEmployeesModal = ({ companyRelatedData, onClose, onSubmit }) => {
  const [preEmployeesFilter, setPreEmployeesFilter] = useState({});
  const [unSelectedEmployees, setUnSelectedEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  return (
    <ModalComponent
      top={ 20 }
      width={ 1200 }
      maskClosable={ false }
      visible={ true }
      footer={ null }
      onCancel={ onClose }
      mainTitle="Add Employees by Filter"
      subTitle="Filter employees and select everyone to include in this payroll"
      className="create-roster-modal"
    >
      <FilterSelectEmployees
        setSelectedEmploymentsIds={ onSubmit }
        initialValues={ preEmployeesFilter }
        companyRelatedData={ companyRelatedData }
        onCancel={ onClose }
        onBack={ onClose }
        employment_ids={ [] }
        moduleName="companyRoaster"
        selected={ selectedEmployees }
        unSelected={ unSelectedEmployees }
        preFilters={ preEmployeesFilter }
        setSelected={ setSelectedEmployees }
        setUnselected={ setUnSelectedEmployees }
        setPrefilters={ setPreEmployeesFilter }
        saveButton
      />
    </ModalComponent>
  );
};

const PayrollEmployeesManager = (props) => {
  const [loader, setLoader] = useState(false);
  const [bulkLoader, setBulkLoader] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    current: 1,
    pageSize: 10,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [columnSearchText, setColumnSearchText] = useState("");
  const [quickMatches, setQuickMatches] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [companyRelatedData, setCompanyRelatedData] = useState(null);
  const [selectedEmploymentId, setSelectedEmploymentId] = useState(null);
  const [childComponent, setChildComponent] = useState(null);

  useEffect(() => {
    getPayrollEmployees();
    getAvailableEmployees();
    // eslint-disable-next-line
  }, [props.record?.id]);

  const getPayrollEmployees = async () => {
    const basePayload = {
      filters: `payroll_|${props.record.id};employee_status_|active`,
      showActive: true,
    };

    setLoader(true);

    try {
      const firstResponse = await getEmployeesReq({ ...basePayload, page: 1 });
      if (!(firstResponse?.code === 200 || firstResponse?.success === true)) {
        onError(null, firstResponse);
        return;
      }

      const firstData = firstResponse?.data?.employee_employments || {};
      const meta = firstData?.meta?.pagination || {};
      const totalPages = meta.total_pages || 1;
      const allEmployees = [...(firstData?.data || [])];

      if (totalPages > 1) {
        const pageRequests = [];
        for (let page = 2; page <= totalPages; page += 1) {
          pageRequests.push(getEmployeesReq({ ...basePayload, page }));
        }

        const pageResponses = await Promise.all(pageRequests);
        pageResponses.forEach((response) => {
          if (response?.code === 200 || response?.success === true) {
            allEmployees.push(...(response?.data?.employee_employments?.data || []));
          }
        });
      }

      onPayrollEmployeesSuccess({
        employee_employments: {
          data: allEmployees,
          meta: {
            pagination: {
              ...meta,
              current_page: 1,
              total: allEmployees.length,
            },
          },
        },
      });
      setCurrentPage(1);
    } catch (error) {
      message.error(error?.message || "Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const onPayrollEmployeesSuccess = (response) => {
    const list = response?.employee_employments?.data || [];
    const meta = response?.employee_employments?.meta?.pagination || {};

    setEmployees(list);
    setPagination({
      total: meta.total || 0,
      current: meta.current_page || currentPage,
      pageSize: meta.per_page || 10,
    });
  };

  const getAvailableEmployees = (search = "") => {
    const payload = {
      page: 1,
      type: "employment",
      filters: search ? `search_|${search};` : "",
    };

    makeRequestStateless(getResourceDataReq, payload, (response) => {
      setAvailableEmployees(response?.resources?.data || []);
    }, () => {
      setAvailableEmployees([]);
    });
  };

  const onError = (err, res) => {
    message.error(res?.response?.data?.message || "Something went wrong");
  };

  const onQuickSearch = (value) => {
    const query = value.trim();

    if (!query) {
      setQuickMatches([]);
      return;
    }

    const payload = {
      page: 1,
      type: "employment",
      filters: `search_|${query};`,
    };

    makeRequestStateless(getResourceDataReq, payload, (response) => {
      const results = response?.resources?.data || [];
      setQuickMatches(results);
      setAvailableEmployees(results);
    }, () => setQuickMatches([]));
  };

  const openManageEmployee = (employmentId) => {
    setChildComponent(
      <PayrollEmployeePolicyEditorModal
        employmentId={ employmentId }
        onClose={ () => {
          setChildComponent(null);
          getPayrollEmployees();
          props.onRefresh?.();
        } }
        onRefresh={ () => {
          getPayrollEmployees();
          props.onRefresh?.();
        } }
      />
    );
  };

  const buildPayrollPolicyPayload = (employmentId, payrollId, data) => ({
    employmentId,
    payroll_info: {
      ...(data?.salary || {}),
      payroll_id: payrollId,
      type: data?.salary?.type || "monthly",
      gross_salary: data?.salary?.gross_salary || 0,
      payment_method_id: data?.salary?.payment_method_id || null,
      policy_id: data?.salary?.policy_id || data?.policy?.policy_planer_id || null,
    },
    salary_benefits: mapSalaryBenefits(data?.salary?.salary_benefits),
  });

  const updateEmployeePayroll = (employmentId, payrollId) => {
    makeRequest(setLoader, getSalaryPolicyByEmpId, employmentId, (data) => {
      const payload = buildPayrollPolicyPayload(employmentId, payrollId, data);

      makeRequest(setLoader, updateSalaryPolicyReq, payload, () => {
        message.success(payrollId ? "Employee added to payroll" : "Employee removed from payroll");
        getPayrollEmployees();
        getAvailableEmployees();
        props.onRefresh?.();
      }, onError);
    }, onError);
  };

  const openBulkAddEmployees = () => {
    if (companyRelatedData) {
      showBulkAddModal(companyRelatedData);
      return;
    }

    setBulkLoader(true);
    makeRequestStateless(getCompanyRelatedDataReq, null, (response) => {
      setCompanyRelatedData(response);
      showBulkAddModal(response);
      setBulkLoader(false);
    }, (err, res) => {
      setBulkLoader(false);
      onError(err, res);
    });
  };

  const showBulkAddModal = (relatedData) => {
    setChildComponent(
      <PayrollBulkEmployeesModal
        companyRelatedData={ relatedData }
        onClose={ () => setChildComponent(null) }
        onSubmit={ bulkAddEmployees }
      />
    );
  };

  const bulkAddEmployees = ({ employment_ids = [] }) => {
    const uniqueEmploymentIds = [...new Set(employment_ids)].filter(Boolean);

    if (uniqueEmploymentIds.length === 0) {
      message.error("Please select at least one employee.");
      return;
    }

    const payload = {
      payroll_id: props.record.id,
      employment_ids: uniqueEmploymentIds,
    };

    makeRequest(setBulkLoader, bulkAssignPayrollEmployeesReq, payload, (response) => {
      const assignedCount = response?.assigned_count || 0;
      const skippedCount = response?.skipped_count || 0;

      if (assignedCount > 0) {
        message.success(`${assignedCount} employee${assignedCount === 1 ? "" : "s"} assigned/updated in payroll`);
      }

      if (skippedCount > 0) {
        message.warning(`${skippedCount} employee${skippedCount === 1 ? "" : "s"} skipped because they are inactive or unavailable.`);
      }

      setChildComponent(null);
      setSelectedEmploymentId(null);
      setColumnSearchText("");
      setQuickMatches([]);
      getPayrollEmployees();
      getAvailableEmployees();
      props.onRefresh?.();
    }, onError);
  };

  const filteredEmployees = useMemo(() => {
    const keyword = (columnSearchText || "").toLowerCase().trim();

    if (!keyword) {
      return employees;
    }

    return employees.filter((employee) => (
      `${employee.full_name_code || ""} ${employee.employee_name || ""} ${employee.code || ""} ${employee.branch_name || ""} ${employee.department_name || ""} ${employee.designation_name || ""}`
        .toLowerCase()
        .includes(keyword)
    ));
  }, [employees, columnSearchText]);

  const applyPayrollEmployeeSearch = () => {
    setCurrentPage(1);
  };

  const resetPayrollEmployeeSearch = () => {
    setColumnSearchText("");
    setCurrentPage(1);
  };

  const assignedEmployeeIds = useMemo(() => employees.map((employee) => employee.id), [employees]);

  const availableEmployeeOptions = useMemo(() => (
    availableEmployees
      .filter((employee) => !assignedEmployeeIds.includes(employee.id))
      .map((employee) => ({
        value: employee.id,
        label: employee.full_name_code || employee.identity || employee.full_name,
      }))
  ), [availableEmployees, assignedEmployeeIds]);

  const columns = [
    {
      title: "Employee",
      key: "employee",
      filterDropdown: () => (
        <div style={ { padding: 8, width: 220 } }>
          <Input
            autoFocus
            allowClear
            placeholder="Filter by name or code"
            value={ columnSearchText }
            onChange={ (event) => setColumnSearchText(event.target.value) }
            onPressEnter={ applyPayrollEmployeeSearch }
          />
          <Space style={ { marginTop: 8, width: "100%", justifyContent: "space-between" } }>
            <Button size="small" type="primary" onClick={ applyPayrollEmployeeSearch }>
              Apply
            </Button>
            <Button size="small" onClick={ resetPayrollEmployeeSearch }>
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: () => <SearchOutlined style={ { color: columnSearchText ? "#1677ff" : undefined } } />,
      render: (record) => (
        <div>
          <div style={ { fontWeight: 600, color: "#111827" } }>{ record.full_name_code || record.employee_name }</div>
          <Text type="secondary">{ record.code ? `Code: ${record.code}` : (record.designation_name || "No designation") }</Text>
        </div>
      ),
    },
    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
    },
    {
      title: "Branch",
      dataIndex: "branch_name",
      key: "branch_name",
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space wrap>
          <Button icon={ <UserSwitchOutlined /> } onClick={ () => openManageEmployee(record.id) }>
            Manage
          </Button>
          <Popconfirm
            title="Remove employee from this payroll?"
            onConfirm={ () => updateEmployeePayroll(record.id, null) }
          >
            <Button danger icon={ <UserDeleteOutlined /> }>Remove</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const footer = [
    <CancelButton key="close_button" onClick={ props.onCompleted } />,
  ];

  return (
    <ModalComponent
      top={ 20 }
      width={ 1280 }
      maskClosable={ false }
      visible={ true }
      footer={ footer }
      onCancel={ props.onCompleted }
      mainTitle={ `${props.record?.name || "Payroll"} Employees` }
      subTitle="View, add, and remove employees from this payroll"
    >
      { childComponent }
      <div className="payroll-workspace__section">
        <Row gutter={ [16, 16] }>
          <Col xs={ 24 } xl={ 8 }>
            <Card
              bordered={ false }
              className="payroll-workspace__detail-card"
              title="Add employee to payroll"
              extra={ <Tag color="blue">{ props.record?.emp_count || 0 } assigned</Tag> }
            >
              <Space direction="vertical" size={ 12 } style={ { width: "100%" } }>
                <Select
                  showSearch
                  allowClear
                  value={ selectedEmploymentId }
                  placeholder="Select employee to add"
                  style={ { width: "100%" } }
                  options={ availableEmployeeOptions }
                  onChange={ (value) => setSelectedEmploymentId(value || null) }
                  onSearch={ (value) => {
                    onQuickSearch(value);
                    getAvailableEmployees(value);
                  } }
                  filterOption={ false }
                  notFoundContent="No employees found"
                />
                {/* <Input
                  prefix={ <SearchOutlined /> }
                  placeholder="Filter current payroll employees"
                  value={ searchText }
                  onChange={ (event) => setSearchText(event.target.value) }
                /> */}
                { quickMatches.length > 0 && (
                  <Card size="small" style={ { background: "#f8fafc", borderRadius: 12 } }>
                    <Space wrap>
                      { quickMatches
                        .filter((employee) => !assignedEmployeeIds.includes(employee.id))
                        .slice(0, 10)
                        .map((employee) => (
                          <Button
                            key={ employee.id }
                            type={ selectedEmploymentId === employee.id ? "primary" : "default" }
                            onClick={ () => setSelectedEmploymentId(employee.id) }
                          >
                            { employee.full_name_code || employee.identity || employee.full_name }
                          </Button>
                        )) }
                    </Space>
                  </Card>
                ) }
                { availableEmployeeOptions.length < 1 && (
                  <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No available employees found to add into this payroll." />
                ) }
                <Space wrap>
                  <Button
                    type="primary"
                    icon={ <PlusOutlined /> }
                    disabled={ !selectedEmploymentId }
                    onClick={ () => updateEmployeePayroll(selectedEmploymentId, props.record.id) }
                  >
                    Add to this payroll
                  </Button>
                  <Button
                    icon={ <FilterOutlined /> }
                    loading={ bulkLoader }
                    onClick={ openBulkAddEmployees }
                  >
                    Add by filter
                  </Button>
                  <Button icon={ <ReloadOutlined /> } onClick={ getPayrollEmployees }>Refresh list</Button>
                </Space>
              </Space>
            </Card>
          </Col>

          <Col xs={ 24 } xl={ 16 }>
            <Card
              bordered={ false }
              className="payroll-workspace__detail-card"
              title="Employees in this payroll"
              extra={ <Tag color="green"><TeamOutlined /> { pagination.total || filteredEmployees.length }</Tag> }
            >
              <Table
                rowKey="id"
                columns={ columns }
                dataSource={ filteredEmployees }
                loading={ loader }
                pagination={ {
                  total: filteredEmployees.length,
                  current: currentPage,
                  pageSize: pagination.pageSize,
                  onChange: setCurrentPage,
                } }
                locale={ {
                  emptyText: <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No employees found in this payroll." />,
                } }
              />
            </Card>
          </Col>
        </Row>
      </div>
    </ModalComponent>
  );
};

export default PayrollEmployeesManager;
